import { Controller } from "@hotwired/stimulus"

/**
 * Manages modal dialogs with backdrop support.
 * Handles focus trapping, Escape key closing, and "static" backdrop behavior.
 */
export default class extends Controller {
  static targets = ["backdrop", "dialog"]
  static values = {
    // If true, clicking the backdrop won't close the modal.
    staticBackdrop: Boolean,
    // If true, Escape key will close the modal.
    keyboard: Boolean
  }

  connect() {
    this.previouslyFocusedElement = document.activeElement
    document.body.classList.add("modal-open")
    this.element.removeAttribute("aria-hidden")

    requestAnimationFrame(() => {
      this.focusInitialElement()
    })
  }

  disconnect() {
    document.body.classList.remove("modal-open")
  }

  /**
   * Closes the modal and restores focus.
   * If inside a turbo-frame, clears the frame content.
   */
  close(event) {
    event?.preventDefault()

    const frame = this.element.closest("turbo-frame")
    if (frame) {
      frame.innerHTML = ""
    } else {
      this.element.remove()
    }

    this.previouslyFocusedElement?.focus?.()
  }

  /**
   * Handles backdrop clicks.
   */
  backdropClose(event) {
    event.preventDefault()

    if (this.staticBackdropValue) {
      this.bounce()
      return
    }

    this.close()
  }

  /**
   * Handles keydown events for Escape (close) and Tab (focus trap).
   */
  keydown(event) {
    if (event.key === "Escape") {
      if (!this.keyboardValue || this.staticBackdropValue) {
        this.bounce()
        return
      }

      this.close()
      return
    }

    if (event.key === "Tab") {
      this.maintainFocus(event)
    }
  }

  /**
   * Triggers a visual "bounce" effect when a static modal is interacted with incorrectly.
   */
  bounce() {
    this.dialogTarget.classList.remove("modal-static")
    requestAnimationFrame(() => {
      this.dialogTarget.classList.add("modal-static")
    })
  }

  /**
   * Automatically focuses the [autofocus] element or the first focusable item.
   */
  focusInitialElement() {
    const autofocusElement = this.element.querySelector("[autofocus]")
    const focusable = this.focusableElements

    if (autofocusElement) {
      autofocusElement.focus()
    } else if (focusable.length > 0) {
      focusable[0].focus()
    } else {
      this.element.focus()
    }
  }

  /**
   * Traps the focus within the modal dialog.
   */
  maintainFocus(event) {
    const focusable = this.focusableElements
    if (focusable.length === 0) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  /**
   * Returns a list of all focusable elements within the modal.
   */
  get focusableElements() {
    return Array.from(
      this.element.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden)
  }
}
